import { createClient } from '@supabase/supabase-js'
import { createDedupingFetch } from './dedupe-fetch'
import type { Database } from './database.types'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'

// Storage adapter natif pour persister la session entre les fermetures d'app
// localStorage est volatile sur iOS/Android — Preferences utilise NSUserDefaults / SharedPreferences
const isNative = Capacitor.isNativePlatform()

// Cache mémoire pour éviter les appels répétés au bridge natif
const memoryCache = new Map<string, string | null>()

const capacitorStorage = {
    async getItem(key: string): Promise<string | null> {
        if (memoryCache.has(key)) {
            return memoryCache.get(key)!
        }
        const { value } = await Preferences.get({ key })
        memoryCache.set(key, value)
        return value
    },
    async setItem(key: string, value: string): Promise<void> {
        memoryCache.set(key, value)
        await Preferences.set({ key, value })
    },
    async removeItem(key: string): Promise<void> {
        memoryCache.set(key, null)
        await Preferences.remove({ key })
    },
}

// Support legacy anon JWT and new publishable key (sb_publishable_...)
// https://github.com/orgs/supabase/discussions/29260
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables (VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY or VITE_SUPABASE_PUBLISHABLE_KEY)')
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
        storage: isNative ? capacitorStorage : undefined,
    },
    realtime: {
        params: {
            eventsPerSecond: 10,
        },
    },
    global: {
        headers: {
            'X-Client-Info': 'harmonia-app',
        },
        // Fusionne les GET identiques deja en vol (cf. dedupe-fetch.ts).
        fetch: createDedupingFetch(),
    },
    db: {
        schema: 'public',
    },
})

// Helper pour appeler les fonctions RPC sans erreur de type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const supabaseRpc = supabase as any

// Note: Auth state changes are now handled centrally in the auth store
// to avoid multiple listeners and potential token refresh conflicts

// Gestion des erreurs de connexion
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 3;

// Fonction pour vérifier la connexion Supabase
export const checkSupabaseConnection = async () => {
    try {
        // Timeout de 3 secondes pour éviter les blocages
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Connection check timeout')), 3000);
        });

        const checkPromise = (async () => {
            // Vérification simple de la session plutôt qu'un appel à la DB
            const { data: { session }, error } = await supabase.auth.getSession();
            if (error) {
                console.error('❌ [Supabase] Session check error:', error);
                return false;
            }

            if (!session) {
                console.error('❌ [Supabase] No active session');
                return false;
            }

            // console.log('✅ [Supabase] Connection healthy (session active)');
            reconnectAttempts = 0; // Reset counter on success
            return true;
        })();

        return await Promise.race([checkPromise, timeoutPromise]);
    } catch (error) {
        console.error('❌ [Supabase] Connection check failed:', error);
        return false;
    }
};

// Fonction pour forcer la reconnexion
export const forceSupabaseReconnect = async () => {
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.error('❌ [Supabase] Max reconnection attempts reached');
        return false;
    }

    reconnectAttempts++;
    // console.log(`🔄 [Supabase] Attempting reconnection (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`);

    try {
        // Forcer un refresh de la session
        const { error } = await supabase.auth.getSession();
        if (error) {
            console.error('❌ [Supabase] Session refresh failed:', error);
            return false;
        }

        // Tester la connexion
        const isConnected = await checkSupabaseConnection();
        if (isConnected) {
            // console.log('✅ [Supabase] Reconnection successful');
            return true;
        }

        return false;
    } catch (error) {
        console.error('❌ [Supabase] Reconnection failed:', error);
        return false;
    }
};

// Helper pour obtenir l'utilisateur connecté
export const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    return user
}

// Helper pour obtenir la session
export const getSession = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    return session
}

/**
 * Identifiant de l'utilisateur courant, lu depuis la session LOCALE.
 *
 * À préférer à getCurrentUser() partout où l'on a seulement besoin d'un id
 * pour filtrer une requête, choisir une clé de cache ou décider d'un rendu.
 * getCurrentUser() passe par auth.getUser(), qui interroge GoTrue sur le
 * réseau à CHAQUE appel : mesuré sur device, c'était 1 à 2 requêtes
 * /auth/v1/user à chaque bascule d'onglet, la plupart pour rien.
 *
 * La sécurité ne repose jamais sur cette valeur : les données restent
 * protégées côté serveur par les RLS, sur chaque requête. Ne garder
 * getCurrentUser() que là où l'on doit VRAIMENT faire valider le jeton par le
 * serveur, typiquement juste après une mutation du compte dont on veut
 * observer l'effet.
 */
export const getCurrentUserId = async (): Promise<string | null> => {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.user?.id ?? null
} 